import { Link } from 'react-router-dom';
import ArticleLayout from '../components/ArticleLayout';

const faqItems = [
  { question: 'Devo avere esperienza per usare Itaobuy Spreadsheet?', answer: 'Assolutamente no! Itaobuy Spreadsheet è progettato per essere intuitivo anche per chi non ha mai fatto shopping online internazionale.' },
  { question: 'Quanto tempo serve per imparare a usarlo?', answer: 'Con questa guida, in soli 10 minuti sarai in grado di fare il tuo primo acquisto. La curva di apprendimento è molto dolce.' },
];

export default function PrincipiantiPage() {
  return (
    <ArticleLayout
      title="Itaobuy Spreadsheet per Principianti: Inizia da Zero in 10 Minuti"
      description="Tutorial completo per chi non ha mai usato Itaobuy Spreadsheet. Dalla registrazione al primo acquisto, tutto spiegato in italiano semplice e chiaro. Nessuna esperienza richiesta."
      tag="Tutorial"
      slug="itaobuy-spreadsheet-per-principianti"
      faqItems={faqItems}
    >
      <div className="bg-accent-50 border border-accent-200/50 rounded-xl p-6 mb-12">
        <p className="text-sm font-semibold text-accent-800 mb-2 font-label">TL;DR &mdash; Per Iniziare Subito</p>
        <p className="text-sm text-accent-700 leading-relaxed">
          Non serve esperienza! In 10 minuti imparerai a navigare su <strong>Itaobuy Spreadsheet</strong>, scegliere
          la categoria giusta, confrontare i prodotti e completare il tuo primo acquisto in sicurezza.
        </p>
      </div>

      <section className="mb-14">
        <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-5">Benvenuto su Itaobuy Spreadsheet</h2>
        <p className="text-foreground-700 leading-relaxed mb-4">
          Se sei nuovo nel mondo dello shopping online internazionale, non preoccuparti. Questa guida è stata scritta
          appositamente per te. Ti accompagnerò passo dopo passo, con un linguaggio semplice e chiaro, fino al tuo
          primo acquisto su <strong>Itaobuy Spreadsheet</strong>.
        </p>
      </section>

      <section className="mb-14">
        <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-5">Minuto 1-2: Familiarizza con l&apos;Interfaccia</h2>
        <p className="text-foreground-700 leading-relaxed mb-4">
          La prima cosa che noterai aprendo Itaobuy Spreadsheet è l&apos;interfaccia organizzata in modo intuitivo.
          In cima trovi il menu di navigazione con le voci principali: <Link to="/" className="text-primary-600 hover:text-primary-700">Home</Link>,
          Guida Acquisti, Categorie, Sicurezza e Blog. Prenditi un minuto per esplorare queste sezioni.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 my-8">
          <div className="bg-background-100 rounded-xl p-5">
            <h3 className="font-heading text-base font-semibold text-foreground-900 mb-2">Menu Categorie</h3>
            <p className="text-sm text-foreground-600">14 categorie ben organizzate: Shoes, Hoodies, T-Shirts e molto altro.</p>
          </div>
          <div className="bg-background-100 rounded-xl p-5">
            <h3 className="font-heading text-base font-semibold text-foreground-900 mb-2">Barra di Ricerca</h3>
            <p className="text-sm text-foreground-600">Cerca prodotti specifici per nome, marca o caratteristiche.</p>
          </div>
        </div>
      </section>

      <section className="mb-14">
        <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-5">Minuto 3-5: La Tua Prima Categoria</h2>
        <p className="text-foreground-700 leading-relaxed mb-4">
          Per il tuo primo acquisto, ti consiglio di iniziare con la categoria <strong>Shoes</strong>. È la più fornita,
          con oltre 5.000 modelli e il maggior numero di recensioni. Clicca sul menu Categorie e seleziona Shoes.
        </p>
        <p className="text-foreground-700 leading-relaxed mb-4">
          Vedrai apparire un elenco organizzato come un foglio di calcolo con colonne per: nome del prodotto,
          prezzo, valutazione a stelle e numero di recensioni. È come avere un catalogo interattivo a portata di click.
        </p>
      </section>

      <section className="mb-14">
        <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-5">Minuto 6-8: Scegliere il Prodotto Giusto</h2>
        <p className="text-foreground-700 leading-relaxed mb-4">
          Ecco i tre criteri fondamentali per scegliere un buon prodotto:
        </p>
        <div className="space-y-5">
          <div className="flex gap-4 bg-background-100 rounded-xl p-5">
            <div className="w-12 h-12 rounded-full bg-accent-100 flex items-center justify-center flex-shrink-0">
              <span className="text-accent-700 font-bold text-lg">1</span>
            </div>
            <div>
              <h3 className="font-heading text-base font-semibold text-foreground-900 mb-1">Valutazione &ge; 4 stelle</h3>
              <p className="text-sm text-foreground-600">Prodotti con almeno 4 stelle hanno superato il test della community.</p>
            </div>
          </div>
          <div className="flex gap-4 bg-background-100 rounded-xl p-5">
            <div className="w-12 h-12 rounded-full bg-accent-100 flex items-center justify-center flex-shrink-0">
              <span className="text-accent-700 font-bold text-lg">2</span>
            </div>
            <div>
              <h3 className="font-heading text-base font-semibold text-foreground-900 mb-1">Almeno 50 recensioni</h3>
              <p className="text-sm text-foreground-600">Più recensioni significano più affidabilità. Diffida dei prodotti con poche valutazioni.</p>
            </div>
          </div>
          <div className="flex gap-4 bg-background-100 rounded-xl p-5">
            <div className="w-12 h-12 rounded-full bg-accent-100 flex items-center justify-center flex-shrink-0">
              <span className="text-accent-700 font-bold text-lg">3</span>
            </div>
            <div>
              <h3 className="font-heading text-base font-semibold text-foreground-900 mb-1">Descrizione Dettagliata</h3>
              <p className="text-sm text-foreground-600">Scegli prodotti con descrizioni complete che includono materiali, taglie e foto reali.</p>
            </div>
          </div>
        </div>
      </section>

      <section className="mb-14">
        <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-5">Minuto 9-10: Il Tuo Primo Acquisto</h2>
        <p className="text-foreground-700 leading-relaxed mb-4">
          Una volta scelto il prodotto, clicca per visualizzare i dettagli. Qui troverai la descrizione completa,
          le foto, le recensioni degli altri utenti e le informazioni sulla spedizione.
        </p>
        <p className="text-foreground-700 leading-relaxed">
          Per completare l&apos;acquisto, segui le istruzioni passo dopo passo. Ricorda di verificare le politiche di
          reso e di utilizzare un metodo di pagamento con protezione acquirente. Consulta anche la nostra
          <Link to="/articoli/itaobuy-spreadsheet-sicurezza" className="text-primary-600 hover:text-primary-700"> guida sulla sicurezza</Link>.
        </p>
      </section>

      <section className="mb-14">
        <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-5">Conclusione: Ce l&apos;hai Fatta!</h2>
        <p className="text-foreground-700 leading-relaxed">
          Complimenti! Hai completato il tuo primo acquisto su <strong>Itaobuy Spreadsheet</strong>. Ora puoi esplorare
          le altre 13 categorie e scoprire nuovi prodotti. Torna alla <Link to="/" className="text-primary-600 hover:text-primary-700 font-medium">home</Link> per
          continuare il tuo viaggio nello shopping intelligente.
        </p>
      </section>
    </ArticleLayout>
  );
}